import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Clock, ExternalLink, LoaderCircle, Pause, Play, RefreshCw, RotateCcw, Save, Search, Send, Trash2, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PipelinePanel } from "@/components/PipelinePanel";
import { WebhookHealthBanner } from "@/components/WebhookHealthBanner";

const PAGE_SIZE = 50;
const STATUS_CHIPS = [
  ["actifs", "À traiter"],
  ["en_attente", "En attente"],
  ["en_cours", "Envoi en cours"],
  ["envoye", "Envoyés"],
  ["echec", "Échecs"],
  ["annule", "Retirés"],
  ["tous", "Tout"],
];

const STATUS = {
  en_attente: { label: "En attente", cls: "status-en_cours" },
  en_cours: { label: "Envoi…", cls: "status-en_cours" },
  echec: { label: "Échec", cls: "status-erreur" },
  envoye: { label: "Envoyé", cls: "status-termine" },
  annule: { label: "Retiré", cls: "status-a_verifier" },
};

function fmt(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function SendQueuePage() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("actifs");
  const [searchParams] = useSearchParams();
  const [ruleFilter, setRuleFilter] = useState(searchParams.get("rule") || "");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [confirmClear, setConfirmClear] = useState(null);
  const [configOpen, setConfigOpen] = useState(() => localStorage.getItem("queue-config-open") === "1");

  const load = async () => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE, status: filter };
    if (ruleFilter) params.rule_id = ruleFilter;
    if (search.trim()) params.q = search.trim();
    const { data: payload } = await api.get("/send-queue", { params });
    setData(payload);
    setConfig((current) => current || payload.config);
  };

  useEffect(() => {
    load().catch(() => toast.error("Impossible de charger la file d’envoi."));
    const timer = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, ruleFilter, search, page]);

  useEffect(() => setPage(0), [filter, ruleFilter, search]);

  useEffect(() => {
    const tick = () => {
      const next = data?.config?.next_send_at ? Math.max(0, Math.round((new Date(data.config.next_send_at) - Date.now()) / 1000)) : null;
      setCountdown(next);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [data]);

  const act = async (fn, successMessage) => {
    try {
      const { data: response } = await fn();
      toast.success(response.message || successMessage);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’action a échoué.");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        min_interval_s: Number(config.min_interval_s),
        max_interval_s: Number(config.max_interval_s),
        daily_cap: Number(config.daily_cap),
        window_start: config.window_start,
        window_end: config.window_end,
        weekdays_only: Boolean(config.weekdays_only),
        enabled: Boolean(config.enabled),
      };
      const { data: response } = await api.patch("/send-queue/config", payload);
      setConfig(response.config);
      toast.success("Réglages d’envoi enregistrés.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  if (!data || !config) return <section className="page">Chargement de la file d’envoi…</section>;
  const rulesSeen = data.rules || [];
  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const live = data.config;
  const paused = live.paused;
  const cooling = live.cooldown_until && new Date(live.cooldown_until) > Date.now();
  const engineState = !live.enabled ? "Désactivé (envoi immédiat)" : paused ? "En pause" : cooling ? "Pause anti-limite Gmail" : !data.in_window ? "Hors plage horaire" : data.remaining_today === 0 ? "Plafond du jour atteint" : data.pending === 0 ? "File vide" : "En cours d’envoi";
  const perHour = Math.round(3600 / ((Number(config.min_interval_s) + Number(config.max_interval_s)) / 2));

  return (
    <section className="page" data-testid="send-queue-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">ENVOI LISSÉ</p>
          <h1 data-testid="send-queue-title">File d’envoi</h1>
          <p className="page-subtitle">Déplacez toutes vos fiches d’un coup dans noCRM : les e-mails partent ici à cadence régulière, dans la plage horaire, sans dépasser le plafond Gmail.</p>
        </div>
        <div className="button-row">
          {paused ? (
            <button className="primary-button" onClick={() => act(() => api.post("/send-queue/resume"), "Envois repris.")} data-testid="queue-resume-button"><Play size={16} /> Reprendre</button>
          ) : (
            <button className="secondary-button" onClick={() => act(() => api.post("/send-queue/pause"), "Envois en pause.")} data-testid="queue-pause-button"><Pause size={16} /> Mettre en pause</button>
          )}
          <button className="icon-action" onClick={() => load()} aria-label="Actualiser" data-testid="queue-refresh-button"><RefreshCw size={18} /></button>
        </div>
      </header>

      <WebhookHealthBanner />

      <div className="stats-grid queue-stats">
        <article className="stat-box" data-testid="queue-stat-pending"><Clock size={19} /><span>En attente</span><strong>{data.pending}</strong></article>
        <article className="stat-box" data-testid="queue-stat-sent-today"><Send size={19} /><span>Envoyés aujourd’hui</span><strong>{data.sent_today} <small>/ {live.daily_cap}</small></strong></article>
        <article className="stat-box" data-testid="queue-stat-failed"><AlertTriangle size={19} /><span>Échecs</span><strong>{data.failed}</strong></article>
        <article className={`stat-box queue-engine ${paused || cooling ? "is-paused" : ""}`} data-testid="queue-stat-engine">
          <Zap size={19} />
          <span>Moteur — {data.paris_time} Paris</span>
          <strong className="engine-state">{engineState}</strong>
          <small data-testid="queue-next-send">
            {engineState === "En cours d’envoi" && countdown !== null ? `Prochain envoi dans ${countdown} s` : cooling ? `Reprise ${fmt(live.cooldown_until)}` : ""}
            {data.eta ? ` · Fin estimée : ${data.eta}` : ""}
          </small>
        </article>
      </div>

      <PipelinePanel selectedRule={ruleFilter} onSelectRule={setRuleFilter} />

      <section className={`settings-section queue-config${configOpen ? "" : " is-collapsed"}`} data-testid="queue-config">
        <button
          type="button"
          className="section-title config-toggle"
          onClick={() => { const next = !configOpen; setConfigOpen(next); localStorage.setItem("queue-config-open", next ? "1" : "0"); }}
          aria-expanded={configOpen}
          data-testid="queue-config-toggle"
        >
          <Clock size={20} />
          <div className="config-toggle-text">
            <p className="eyebrow">CADENCE</p>
            <h2>Réglages de cadence</h2>
            {!configOpen && (
              <p className="config-summary" data-testid="queue-config-summary">
                {live.min_interval_s}-{live.max_interval_s} s · ≈ {Math.round(3600 / ((Number(live.min_interval_s) + Number(live.max_interval_s)) / 2))} e-mails/h · plafond {live.daily_cap}/jour · {live.window_start}-{live.window_end}{live.weekdays_only ? " · jours ouvrés" : ""}{live.enabled ? "" : " · envoi lissé désactivé"}
              </p>
            )}
          </div>
          <ChevronDown size={18} className={`config-chevron${configOpen ? " is-open" : ""}`} />
        </button>
        {configOpen && <>
        <div className="form-grid queue-form">
          <label>Intervalle min (s)<input type="number" min="5" value={config.min_interval_s} onChange={(e) => setConfig({ ...config, min_interval_s: e.target.value })} data-testid="queue-min-interval" /></label>
          <label>Intervalle max (s)<input type="number" min="5" value={config.max_interval_s} onChange={(e) => setConfig({ ...config, max_interval_s: e.target.value })} data-testid="queue-max-interval" /></label>
          <label>Plafond par jour<input type="number" min="1" value={config.daily_cap} onChange={(e) => setConfig({ ...config, daily_cap: e.target.value })} data-testid="queue-daily-cap" /></label>
          <label>Début (heure Paris)<input type="time" value={config.window_start} onChange={(e) => setConfig({ ...config, window_start: e.target.value })} data-testid="queue-window-start" /></label>
          <label>Fin (heure Paris)<input type="time" value={config.window_end} onChange={(e) => setConfig({ ...config, window_end: e.target.value })} data-testid="queue-window-end" /></label>
          <label className="queue-check"><input type="checkbox" checked={Boolean(config.weekdays_only)} onChange={(e) => setConfig({ ...config, weekdays_only: e.target.checked })} data-testid="queue-weekdays-only" />Jours ouvrés uniquement</label>
          <label className="queue-check"><input type="checkbox" checked={Boolean(config.enabled)} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} data-testid="queue-enabled" />Envoi lissé activé (décoché = envoi immédiat comme avant)</label>
        </div>
        <p className="field-help" data-testid="queue-rate-summary">≈ {perHour} e-mails / heure · Workspace autorise 2 000 / jour : gardez une marge (1 800 conseillé).</p>
        <div className="button-row">
          <button className="primary-button" onClick={saveConfig} disabled={saving} data-testid="queue-save-config"><Save size={16} />{saving ? "Enregistrement…" : "Enregistrer les réglages"}</button>
        </div>
        </>}
      </section>

      <section className="data-panel" data-testid="queue-items-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">FILE</p>
            <h2>Envois programmés</h2>
          </div>
          <div className="journal-controls">
            <label className="commune-search">
              <Search size={15} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une mairie…" data-testid="queue-search-input" />
            </label>
            {data.failed > 0 && <button className="secondary-button" onClick={() => act(() => api.post("/send-queue/retry-failed"))} data-testid="queue-retry-failed"><RotateCcw size={14} /> Remettre les échecs en file</button>}
            {data.pending > 0 && <button className="danger-button queue-clear" onClick={() => setConfirmClear("all")} data-testid="queue-clear-button"><Trash2 size={14} /> Vider la file</button>}
          </div>
        </div>

        <div className="chip-filters" data-testid="queue-filters">
          <div className="chip-row">
            <span className="chip-label">Étape</span>
            <button type="button" className={`chip${!ruleFilter ? " active" : ""}`} onClick={() => setRuleFilter("")} data-testid="queue-rule-chip-all">
              Toutes <small>{Object.values(data.counts?.by_rule || {}).reduce((sum, n) => sum + n, 0)}</small>
            </button>
            {rulesSeen.map((rule) => (
              <button type="button" key={rule.rule_id} className={`chip${ruleFilter === rule.rule_id ? " active" : ""}`} onClick={() => setRuleFilter(ruleFilter === rule.rule_id ? "" : rule.rule_id)} data-testid={`queue-rule-chip-${rule.rule_id}`}>
                {rule.step_name || rule.rule_name} <small>{data.counts?.by_rule?.[rule.rule_id] || 0}</small>
              </button>
            ))}
          </div>
          <div className="chip-row">
            <span className="chip-label">Statut</span>
            {STATUS_CHIPS.map(([key, label]) => (
              <button type="button" key={key} className={`chip${filter === key ? " active" : ""}`} onClick={() => setFilter(key)} data-testid={`queue-status-chip-${key}`}>
                {label} <small>{data.counts?.by_status?.[key] || 0}</small>
              </button>
            ))}
          </div>
          <div className="chip-row chip-summary">
            <span className="table-count" data-testid="queue-results-count">{data.total} résultat{data.total > 1 ? "s" : ""}{pageCount > 1 ? ` · page ${page + 1}/${pageCount}` : ""}</span>
            {(filter === "en_attente" || filter === "actifs") && data.total > 0 && (ruleFilter || search.trim()) && (
              <button type="button" className="link-button danger-link" onClick={() => setConfirmClear("filtered")} data-testid="queue-clear-filtered-button">
                <Trash2 size={12} /> Retirer les envois en attente de cette sélection
              </button>
            )}
          </div>
        </div>

        <div className="table-wrap">
          <table data-testid="queue-table">
            <thead><tr><th>#</th><th>Fiche noCRM</th><th>Déplacée vers</th><th>Déplacée le</th><th>{filter === "envoye" ? "Envoyée le" : "En file depuis"}</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan="7" className="empty-cell" data-testid="queue-empty">Aucun envoi dans cette vue.</td></tr>}
              {data.items.map((item, index) => (
                <tr key={item.id} data-testid={`queue-row-${item.id}`}>
                  <td className="mono">{page * PAGE_SIZE + index + 1}</td>
                  <td>
                    {item.lead_url ? (
                      <a href={item.lead_url} target="_blank" rel="noreferrer" className="lead-link" title="Ouvrir la fiche dans noCRM" data-testid={`queue-lead-link-${item.id}`}>
                        {item.lead_title || item.recipient_email || `Lead ${item.lead_id}`} <ExternalLink size={11} />
                      </a>
                    ) : (item.lead_title || item.recipient_email || `Lead ${item.lead_id}`)}
                    {item.error && <div className="review-reason-inline error"><AlertTriangle size={12} /><span>{item.error}</span></div>}
                  </td>
                  <td data-testid={`queue-step-${item.id}`}>{item.step_name || item.rule_name || item.rule_id}</td>
                  <td className="mono" data-testid={`queue-moved-${item.id}`}>{fmt(item.moved_at || item.created_at)}</td>
                  <td className="mono">{fmt(filter === "envoye" ? item.sent_at : item.created_at)}</td>
                  <td><span className={`status-badge ${STATUS[item.status]?.cls || ""}`}>{item.status === "en_cours" && <LoaderCircle size={12} className="spinning" />} {STATUS[item.status]?.label || item.status}</span></td>
                  <td className="list-actions">
                    {(item.status === "en_attente" || item.status === "echec") && (
                      <>
                        <button className="secondary-button" onClick={() => act(() => api.post(`/send-queue/items/${item.id}/send-now`))} title="Envoyer maintenant" data-testid={`queue-send-now-${item.id}`}><Send size={13} /></button>
                        <button className="icon-action danger-icon" onClick={() => act(() => api.delete(`/send-queue/items/${item.id}`))} title="Retirer de la file" aria-label="Retirer" data-testid={`queue-remove-${item.id}`}><Trash2 size={13} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="pagination" data-testid="queue-pagination">
            <button type="button" className="secondary-button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="queue-prev-page"><ChevronLeft size={14} /> Précédent</button>
            <span className="table-count">Page {page + 1} / {pageCount}</span>
            <button type="button" className="secondary-button" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)} data-testid="queue-next-page">Suivant <ChevronRight size={14} /></button>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(confirmClear)}
        title={confirmClear === "all" ? `Retirer les ${data.pending} envois en attente ?` : `Retirer les ${data.counts?.by_status?.en_attente || 0} envois en attente de cette sélection ?`}
        description="Les e-mails ne seront pas envoyés. Les fiches noCRM restent à leur étape actuelle : il faudra les redéplacer pour relancer."
        confirmLabel={confirmClear === "all" ? "Vider la file" : "Retirer la sélection"}
        onConfirm={async () => {
          const params = {};
          if (ruleFilter) params.rule_id = ruleFilter;
          if (search.trim()) params.q = search.trim();
          await act(() => (confirmClear === "all" ? api.delete("/send-queue/items") : api.delete("/send-queue/items/filtered", { params })));
          setConfirmClear(null);
        }}
        onCancel={() => setConfirmClear(null)}
      />
    </section>
  );
}
